// src/services/chatApi.js
import i18n from 'i18next';

// Helper to get authentication headers
const getAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Always include Content-Type for consistency in POST/DELETE requests.
    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
        // This log is very useful for debugging.
        console.warn("Auth token not found in localStorage. Request will be sent without Authorization header.");
    }
    
    return headers;
};

export const getFormDataAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Start with an empty headers object
    const headers = {};

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Notice: We NEVER add Content-Type here.
    // The browser will handle it for FormData.
    return headers;
};

// const handleResponse = async (response) => {
//     if (!response.ok) {
//         const errorData = await response.json().catch(() => ({
//             detail: `Request failed with status ${response.status}`
//         }));
//         throw new Error(errorData.detail || 'Request failed');
//     }
    
//     // For 204 No Content
//     if (response.status === 204) {
//         return null;
//     }
    
//     return response.json();
// };

// A new helper function to handle 401 errors globally
const handleUnauthorized = () => {
    // This is a robust way to handle expired sessions.
    console.error("Authentication failed (401). Session may be expired. Redirecting to login.");
    // Clear potentially bad tokens from storage.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token'); // If you use refresh tokens
    // Redirect the user to the login page.
    window.location.href = '/login';
};


// const API_BASE_URL = 'http://localhost:8000/api/ai-tools';

// const API_USERS_URL = 'http://localhost:8000/api/users';

// const API_BASE_URL = '/api/ai-tools';
// const API_USERS_URL = '/api/users';

const DOMAIN = process.env.REACT_APP_API_URL || '';

// Construct the full URL: https://api.ugotai.com/api/ai-tools
const API_BASE_URL = `${DOMAIN}/ai-tools`;

let isRefreshing = false;
// This queue will hold all API calls that fail while a token refresh is in progress.
let failedQueue = [];

// --- Helper function to process the queue ---
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error); // Reject all queued requests if refresh fails
        } else {
            prom.resolve(token); // Resolve all queued requests with the new token
        }
    });
    failedQueue = []; // Clear the queue
};




// A wrapper for fetch to handle errors consistently
export const apiFetch = async (url, options = {}) => {
    // 1. Prepare headers and add the current access token
    const token = localStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // 2. Make the initial API call
    const response = await fetch(url, { ...options, headers });

    // 3. Check for a 401 Unauthorized error (expired access token)
    if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            handleUnauthorized(); // No refresh token, must log out
            return Promise.reject(new Error('Unauthorized: No refresh token'));
        }

        // If a refresh is already happening, queue this request and wait
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({
                    resolve: (newToken) => {
                        // When the refresh is done, this will be called.
                        // We retry the original request with the new token.
                        const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newToken}` } };
                        resolve(apiFetch(url, newOptions)); // Recursively call apiFetch to re-process
                    },
                    reject,
                });
            });
        }

        // This is the first request to fail, so let's start the refresh process
        isRefreshing = true;

        try {
            // 4. Send the request to your refresh token endpoint
            // !!! IMPORTANT: Adjust this URL to match your Django project's refresh token URL !!!
            const refreshResponse = await fetch(`${API_BASE_URL}/users/token/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });

            if (!refreshResponse.ok) {
                // If the refresh token is invalid (e.g., expired or blacklisted), logout.
                throw new Error('Refresh token is invalid.');
            }

            const { access: newAccessToken } = await refreshResponse.json();
            
            // 5. Store the new token and process the queue
            localStorage.setItem('access_token', newAccessToken);
            processQueue(null, newAccessToken); // Process queued requests with the new token

            // 6. Retry the original failed request with the new token
            const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newAccessToken}` } };
            return apiFetch(url, newOptions); // Retry the original request

        } catch (error) {
            // If the refresh process fails, reject all queued requests and log out.
            processQueue(error, null);
            handleUnauthorized();
            return Promise.reject(error);
        } finally {
            // 7. ALWAYS unlock the refreshing state
            isRefreshing = false;
        }
    }

    // --- If the response was not 401, handle it normally ---
    if (!response.ok) {
        // This handles all other errors (400, 404, 500, etc.)
        const errorData = await response.json().catch(() => ({ detail: `Request failed with status ${response.status}` }));
        throw new Error(errorData.detail || 'An unknown API error occurred');
    }

    // For DELETE or other requests that might not return a JSON body
    if (response.status === 204) {
        return {}; // Return an empty object for consistency
    }

    return response.json(); // Return the parsed JSON for successful requests
};

export const checkTaskStatus = async (taskId) => {
    const response = await fetch(`${API_BASE_URL}/audio/tasks/status/${taskId}/`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) {
        try {
            // Attempt to parse JSON error response
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            // If JSON parsing fails, try to get text response for debugging
            try {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server error (${response.status}): ${errorText.substring(0, 200)}...`);
            } catch (textError) {
                // If both JSON and text fail, throw generic error
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }
    }

    // For successful responses, handle potential JSON parsing errors
    try {
        return await response.json();
    } catch (jsonError) {
        console.error('Failed to parse JSON response:', response);
        throw new Error('Invalid JSON response from server');
    }
};

// Audio summariser

// src/services/videoApi.js (or create audioApi.js)

export const summarizeAudio = async (audioFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/summarize/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
    }
    return await response.json();
};

// =========== generate audio ===============

export const generateAudio = async (text, voice, format) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    
    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/generate/`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(), // Include auth headers
            'Content-Type': 'application/json',
            'Accept-Language': language, // Send language as a header
        },
        body: JSON.stringify({
            text,
            voice,
            format
        })
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Generation failed');
    }
    return await response.json();
};

// audio to text

export const transcribeAudio = async (audioFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/transcribe/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Transcription upload failed');
    }
    return await response.json();
};

// add background to audio

export const getDefaultAudioUrl = async () => {
    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/default-bg-preview/`, {
        method: 'GET',
        headers: getFormDataAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get preview');
    return await response.json();
};

export const addBackgroundToAudio = async (mainAudioFile, bgAudioFile, volumeReduction) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    
    formData.append('main_audio', mainAudioFile);
    if (bgAudioFile) {
        formData.append('bg_audio', bgAudioFile);
    }
    formData.append('volume_reduction', volumeReduction); // 0 to 40

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/add-background/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
    }
    return await response.json();
};

// trim audio

export const trimAndMergeAudio = async (audioFile, segments) => {
    // segments is an array of arrays: [[start, end], [start, end]]
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    
    formData.append('audio', audioFile);
    formData.append('segments', JSON.stringify(segments));

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/trim-merge/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Processing failed');
    }
    return await response.json();
};

// =============== merge audio  ===================

export const mergeAudioFiles = async (audioFiles) => {
    // audioFiles is an array of File objects
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    
    // Append each file with the same key 'audios'
    audioFiles.forEach((file) => {
        formData.append('audios', file);
    });

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/merge/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Processing failed');
    }
    return await response.json();
};

// convert audio format

export const convertAudio = async (audioFile, targetFormat) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    
    formData.append('audio', audioFile);
    formData.append('format', targetFormat);

    const response = await fetch(`${API_BASE_URL}/audio/audio-tools/convert/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Conversion failed');
    }
    return await response.json();
};
